import { isNil } from 'lodash';
import { DateTime } from '~domain/model/core/date/datetime';
import { CreatePolicy } from '~domain/model/policy/create-policy';
import { GetManyPoliciesOptions } from '~domain/model/policy/get-many-policies-options';
import { Policy } from '~domain/model/policy/policy';
import { PolicyInitialValues } from '~domain/model/policy/policy-initial-values';
import { PolicySummary } from '~domain/model/policy/policy-summary';
import { BaseClient, DEFAULT_JSON_API_HEADERS, Headers, QueryParams } from './base-client';
import { RequestErrorHandler } from './request-error-handlers/base-request-error-handler';

export class PoliciesClient extends BaseClient {
  constructor(baseUrl: string, headers: Headers = {}, requestErrorHandler?: RequestErrorHandler) {
    super(baseUrl, { ...DEFAULT_JSON_API_HEADERS, ...headers }, requestErrorHandler);
  }

  getPolicies(accessToken: string, options?: GetManyPoliciesOptions): Promise<PolicySummary[]> {
    const queryParams: QueryParams = {};
    if (!isNil(options.userAccountId)) {
      queryParams.userAccountId = options.userAccountId;
    }

    if (!isNil(options.issuances)) {
      queryParams.issuances = options.issuances;
    }

    if (!isNil(options.expirationDateGreaterThanOrEqualTo)) {
      queryParams.expirationDateGreaterThanOrEqualTo = options.expirationDateGreaterThanOrEqualTo.formatToISO8601();
    }

    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<PolicySummary[]>('', queryParams).toPromise();
  }

  getPolicy(accessToken: string, policyId: string) {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<PolicySummary>(policyId).toPromise();
  }

  create(apiKey: string, policy: CreatePolicy) {
    const headers = { 'x-api-key': apiKey };

    return this.create$<Policy>('', policy, undefined, headers).toPromise();
  }

  update(apiKey: string, policyId: string, policy: Partial<PolicyInitialValues>) {
    const headers = { 'x-api-key': apiKey };

    return this.updatePartially$<Policy>(`${policyId}`, policy, undefined, headers).toPromise();
  }

  async generateRenewalRateReport(accessToken: string, from: DateTime, to: DateTime) {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    await this.create$<Buffer>(
      'report/renewal-rate',
      {
        from: from.formatToISO8601(),
        to: to.formatToISO8601(),
      },
      {},
      {},
    ).toPromise();
  }

  async generateCountReport(accessToken: string, from: DateTime, to: DateTime) {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    await this.create$<Buffer>(
      'report/count',
      {
        from: from.formatToISO8601(),
        to: to.formatToISO8601(),
      },
      {},
      {},
    ).toPromise();
  }
}
